import React, { useState, useEffect, useRef } from 'react';
import { Layout, CreditCard, Film, ExternalLink, LogOut, LogIn, UserPlus, Settings, ChevronDown, Menu, X as CloseIcon, User as UserIcon } from '@geist-ui/icons';
import Dashboard from './pages/Dashboard';
import LandingPage from './pages/LandingPage'; // Import LandingPage
// Removed MUI imports: AppBar, Toolbar, Typography, Button, Box, CircularProgress
import { Routes, Route, Link, Navigate, BrowserRouter } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SubscriptionPage from './pages/SubscriptionPage';
// import VideoEditorLayoutPage from './pages/VideoEditorLayoutPage'; // Removed as the file was deleted
import ClipGalleryPage from './pages/ClipGalleryPage';
import ClipListPage from './pages/ClipListPage'; // Import the new ClipListPage
import TermsOfUsePage from './pages/TermsOfUsePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import ShippingPolicyPage from './pages/ShippingPolicyPage';
import LinkAccountPage from './pages/LinkAccountPage'; // Import LinkAccountPage
import DiscoverAccountPage from './pages/DiscoverAccountPage'; // Import DiscoverAccountPage
import AccountSettingsPage from './pages/AccountSettingsPage'; // Import AccountSettingsPage
import MyVodsPage from './pages/MyVodsPage'; // Import MyVodsPage
import KickCallbackPage from './pages/KickCallbackPage'; // Import KickCallbackPage
import GoogleCallbackPage from './pages/GoogleCallbackPage'; // Import GoogleCallbackPage
import TwitchCallbackPage from './pages/TwitchCallbackPage'; // Import TwitchCallbackPage
import PricingPage from './pages/PricingPage'; // Import PricingPage
import OAuthRegisterPage from './pages/OAuthRegisterPage'; // Import OAuthRegisterPage
import Footer from './components/Footer'; // Import Footer
import { AuthProvider, useAuth } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { SocketProvider } from './context/SocketContext';
import { JobProgressProvider } from './context/JobProgressContext'; // Import JobProgressProvider

// Protected Route component (no MUI needed)
interface ProtectedRouteProps {
  element: React.ReactNode;
  adminOnly?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element, adminOnly = false }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      // Replaced MUI Box and CircularProgress with Tailwind styled div
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && user?.role !== 'ADMIN') {
    return <Navigate to="/" />;
  }

  // When user.id changes (e.g., after login or logout),
  // this key will change, forcing the Dashboard (or any protected element) to remount.
  // This ensures a fresh state and useEffect calls for the Dashboard.
  return React.cloneElement(element as React.ReactElement, { key: user?.id || 'loggedout' });
};

// Tailwind Refactored App Bar component
const AppHeader: React.FC = () => {
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const [isUserMenuOpen, setUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false); // State for mobile menu
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null); // Ref for mobile menu

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node) && isMobileMenuOpen) {
        // Check if the click is on the hamburger button itself to prevent immediate closing
        const hamburgerButton = document.getElementById('hamburger-button');
        if (hamburgerButton && hamburgerButton.contains(event.target as Node)) {
          return;
        }
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);
  const closeUserMenu = () => setUserMenuOpen(false);

  const commonLinkClasses = "flex items-center text-foreground hover:text-primary hover:bg-secondary/50 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ease-in-out";
  const mobileLinkClasses = "flex items-center text-foreground hover:text-primary hover:bg-secondary/50 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ease-in-out w-full";


  return (
    <header className="bg-card shadow-md border-b border-border sticky top-0 z-40">
      <nav className="container mx-auto px-4 py-2 flex items-center justify-between h-16">
        {/* Logo */}
        <div className="flex items-center">
          <Link to="/" onClick={closeMobileMenu}>
            <img
              src={`/assets/clipoid_logo.png?v=${new Date().getTime()}`}
              alt="Clipoid Logo"
              className="h-10 md:h-12"
            />
          </Link>
        </div>

        {/* Desktop Navigation Links */}
        <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
          {isLoading ? (
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
          ) : isAuthenticated ? (
            <>
              <Link to="/dashboard" className={commonLinkClasses}>
                <Layout size={18} className="mr-2" /> Dashboard
              </Link>
              {/* Subscription link removed from main nav */}
              <Link to="/clips" className={commonLinkClasses}>
                <Film size={18} className="mr-2" /> Gallery
              </Link>
              <Link to="/my-vods" className={commonLinkClasses}>
                <Layout size={18} className="mr-2" /> Twitch VODs
              </Link>
              {/* Clip Editor link - Tokenlar artık localStorage'da değil, cookie'de yönetiliyor. */}
              {/* Bu linkin doğru çalışması için backend destekli bir çözüm gerekebilir. */}
              {/* Şimdilik token'sız direkt link veriyoruz ve uyarıyı kaldırıyoruz. */}
              <a href="http://editor.clipoid.com/" target="_blank" rel="noopener noreferrer" className={commonLinkClasses}>
                <ExternalLink size={18} className="mr-2" /> Clip Editor
              </a>
              {/* User Menu Dropdown (Desktop) */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center text-muted-foreground hover:text-primary px-3 py-2 text-sm font-medium rounded-lg hover:bg-secondary/50 transition-all duration-200 ease-in-out"
                >
                  {user?.profileImageUrl ? (
                    <img
                      src={user.profileImageUrl}
                      alt={user.username || 'User Avatar'}
                      className="w-6 h-6 rounded-full mr-2 object-cover"
                    />
                  ) : (
                    <UserIcon size={18} className="mr-2 text-muted-foreground" />
                  )}
                  {user?.username || 'Account'}
                  <ChevronDown size={16} className={`ml-1 transform transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-card rounded-md shadow-lg py-1 z-50 border border-border">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-semibold text-foreground truncate">{user?.username}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                    <Link
                      to="/account-settings"
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-foreground hover:bg-secondary/50 hover:text-primary transition-colors duration-150"
                      onClick={closeUserMenu}
                    >
                      <Settings size={16} className="mr-3 text-muted-foreground" /> Account Settings
                    </Link>
                    <Link
                      to="/subscription"
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-foreground hover:bg-secondary/50 hover:text-primary transition-colors duration-150"
                      onClick={closeUserMenu}
                    >
                      <CreditCard size={16} className="mr-3 text-muted-foreground" /> Subscription
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        closeUserMenu();
                      }}
                      className="w-full text-left flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-500/10 hover:text-red-700 transition-colors duration-150"
                    >
                      <LogOut size={16} className="mr-3" /> Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/pricing" className={commonLinkClasses}>
                <CreditCard size={18} className="mr-2" /> Pricing
              </Link>
              <Link to="/login" className={commonLinkClasses}>
                <LogIn size={18} className="mr-2" /> Login
              </Link>
              <Link to="/register" className="flex items-center text-primary-foreground bg-primary hover:bg-primary/90 px-3 py-2 rounded-lg text-sm font-semibold shadow-md transition-all duration-200 ease-in-out transform hover:scale-105">
                <UserPlus size={18} className="mr-2" /> Sign Up
              </Link>
            </>
          )}
        </div>

        {/* Hamburger Menu Button (Mobile) */}
        <div className="md:hidden flex items-center">
          <button
            id="hamburger-button"
            onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
            className="text-foreground hover:text-primary p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
            aria-label="Open main menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <CloseIcon size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div ref={mobileMenuRef} className="md:hidden absolute top-16 left-0 right-0 bg-card shadow-lg z-30 border-b border-border">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {isLoading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : isAuthenticated ? (
              <>
                <Link to="/dashboard" className={mobileLinkClasses} onClick={closeMobileMenu}>
                  <Layout size={20} className="mr-3" /> Dashboard
                </Link>
                {/* Subscription link removed from main mobile nav */}
                <Link to="/clips" className={mobileLinkClasses} onClick={closeMobileMenu}>
                  <Film size={20} className="mr-3" /> Gallery
                </Link>
                <Link to="/my-vods" className={mobileLinkClasses} onClick={closeMobileMenu}>
                  <Layout size={20} className="mr-3" /> Twitch VODs
                </Link>
                {/* Clip Editor link - Tokenlar artık localStorage'da değil, cookie'de yönetiliyor. */}
                {/* Bu linkin doğru çalışması için backend destekli bir çözüm gerekebilir. */}
                {/* Şimdilik token'sız direkt link veriyoruz. */}
                <a href="http://editor.clipoid.com/" target="_blank" rel="noopener noreferrer" className={mobileLinkClasses} onClick={closeMobileMenu}>
                  <ExternalLink size={20} className="mr-3" /> Clip Editor
                </a>
                {/* User Info and Actions in Mobile Menu */}
                <div className="pt-4 pb-3 border-t border-border">
                  <div className="flex items-center px-4 mb-2">
                    {user?.profileImageUrl ? (
                      <img
                        src={user.profileImageUrl}
                        alt={user.username || 'User Avatar'}
                        className="w-10 h-10 rounded-full mr-3 object-cover"
                      />
                    ) : (
                      <UserIcon size={36} className="mr-3 text-muted-foreground" />
                    )}
                    <div>
                      <p className="text-base font-medium text-foreground">{user?.username || 'Account'}</p>
                      <p className="text-sm font-medium text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <Link
                    to="/account-settings"
                    className={mobileLinkClasses}
                    onClick={closeMobileMenu}
                  >
                    <Settings size={20} className="mr-3 text-muted-foreground" /> Account Settings
                  </Link>
                  <Link
                    to="/subscription"
                    className={mobileLinkClasses}
                    onClick={closeMobileMenu}
                  >
                    <CreditCard size={20} className="mr-3 text-muted-foreground" /> Subscription
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      closeMobileMenu();
                    }}
                    className={`${mobileLinkClasses} text-red-600 hover:text-red-700 hover:bg-red-500/10`}
                  >
                    <LogOut size={20} className="mr-3" /> Logout
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link to="/pricing" className={mobileLinkClasses} onClick={closeMobileMenu}>
                  <CreditCard size={20} className="mr-3" /> Pricing
                </Link>
                <Link to="/login" className={mobileLinkClasses} onClick={closeMobileMenu}>
                  <LogIn size={20} className="mr-3" /> Login
                </Link>
                <Link to="/register" className={`${mobileLinkClasses} bg-primary text-primary-foreground hover:bg-primary/90`} onClick={closeMobileMenu}>
                  <UserPlus size={20} className="mr-3" /> Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

function AppContent() {
  return (
    // Replaced MUI Box with div and applied Tailwind classes
    <div className="text-white min-h-screen flex flex-col bg-background"> {/* Use Tailwind's bg-background which maps to CSS var */}
      <AppHeader />
      {/* Replaced MUI Box with main and added padding */}
      <main className="flex-grow"> {/* Tüm paddingler kaldırıldı */}
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/subscription" element={<ProtectedRoute element={<SubscriptionPage />} />} />
          <Route path="/clips" element={<ProtectedRoute element={<ClipGalleryPage />} />} />
          <Route path="/clips/:jobId" element={<ProtectedRoute element={<ClipListPage />} />} />
          {/* <Route path="/edit-clip/:clipId" element={<ProtectedRoute element={<VideoEditorLayoutPage />} />} /> */} {/* Removed route for deleted page */}
          {/* Admin route placeholder */}
          {/* <Route path="/admin" element={<ProtectedRoute element={<AdminPage />} adminOnly={true} />} /> */}
          <Route path="/terms" element={<TermsOfUsePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/refund" element={<RefundPolicyPage />} />
          <Route path="/shipping" element={<ShippingPolicyPage />} />
                    <Route path="/link-account" element={<LinkAccountPage />} />
          <Route path="/discover-account" element={<DiscoverAccountPage />} /> {/* Add Discover Account route */}
          <Route path="/account-settings" element={<ProtectedRoute element={<AccountSettingsPage />} />} />
          <Route path="/my-vods" element={<ProtectedRoute element={<MyVodsPage />} />} />
          <Route path="/pricing" element={<PricingPage />} /> {/* Add Pricing route */}
          <Route path="/oauth-register" element={<OAuthRegisterPage />} /> {/* Add OAuth Register route */}
        </Routes>
      </main>
      <Footer /> {/* Add Footer here */}
    </div>
  );
}

function App() {
  return (
    <Routes>
      {/* Callback routes WITHOUT AuthProvider to prevent popup auth conflicts */}
      <Route path="/auth/kick/callback" element={<KickCallbackPage />} />
      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
      <Route path="/auth/twitch/callback" element={<TwitchCallbackPage />} />
      
      {/* All other routes WITH AuthProvider */}
      <Route path="/*" element={
        <AuthProvider>
          <JobProgressProvider>
            <SocketProvider>
              <SubscriptionProvider>
                <AppContent />
              </SubscriptionProvider>
            </SocketProvider>
          </JobProgressProvider>
        </AuthProvider>
      } />
    </Routes>
  );
}

export default App;